import { useEffect, useRef } from 'react'
import {
  MessagePartPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAui,
} from '@assistant-ui/react'

/**
 * The conversation, built from primitives.
 *
 * assistant-ui ships styled components, but they assume Tailwind and this project writes
 * its own CSS — and the visual target here is a financial terminal rather than a chat app,
 * so the styling was never going to be inherited anyway.
 *
 * There is no composer. Onboarding is a decision tree with enumerable answers, so a text
 * box would invite input the script cannot honour and would read as a broken chatbot. When
 * the assistant becomes a real one, the composer arrives with it.
 */
export function OnboardingThread() {
  const aui = useAui()
  const started = useRef(false)

  // The assistant opens the conversation, so something has to prompt the first turn.
  //
  // Guarded with a ref rather than by checking whether the thread is empty. React invokes
  // effects twice in development, and both invocations run before either append lands — so
  // an emptiness check passes twice and the conversation opens with the welcome said
  // twice. A ref is set synchronously and so is actually exclusive.
  useEffect(() => {
    if (started.current) return
    started.current = true
    void aui.thread.append({ role: 'user', content: [{ type: 'text', text: 'begin' }] })
  }, [aui])

  return (
    <ThreadPrimitive.Root className="aui-thread">
      <ThreadPrimitive.Viewport className="aui-viewport">
        <ThreadPrimitive.Messages components={{ AssistantMessage, UserMessage }} />
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  )
}

function AssistantMessage() {
  return (
    <MessagePrimitive.Root className="aui-message is-assistant">
      <MessagePrimitive.Parts components={{ Text: AssistantText }} />
    </MessagePrimitive.Root>
  )
}

function AssistantText() {
  return (
    <p className="aui-text">
      <MessagePartPrimitive.Text />
    </p>
  )
}

/**
 * The turn that starts the conversation carries no meaning, so it is not shown. Rendering
 * "begin" as though the user had said it would be a lie about how the exchange started.
 */
function UserMessage() {
  return null
}
